// The influence overlay's link geometry: how a provenance edge between two
// cells is drawn, and how hard.
//
// Extracted from the view because the curve has one rule that is easy to break
// and invisible when broken -- the handles must never overshoot the midpoint --
// and because the straight-line case exists only for links whose two cells share
// a turn bucket, which is a fact about the axis rather than about SVG.

// Weight, not colour: tier drives the hue in the view (it is a CSS token there),
// and how firmly the claim is drawn here.
export const tierStroke = (tier) => (tier === "adjacent" ? 1 : tier === "join" ? 1.4 : 1.6);
export const tierOpacity = (tier) => (tier === "adjacent" ? 0.45 : tier === "join" ? 0.7 : 0.8);
// Strongest first, for choosing which links survive the draw cap.
export const TIER_STRENGTH = { cited: 0, quoted: 1, entity: 2, adjacent: 3 };
// A cap that says nothing reads as "this is all of it". What a cell FED has no
// ceiling upstream -- one widely-quoted source can feed every later production --
// so the draw takes the strongest and reports how many it set aside.
export const INFLUENCE_DRAW_CAP = 24;

// A link's path, plus the tangent its arrowhead sits on. Horizontal handles
// keep the curve flat enough to read across a tall span; when the two cells
// share a turn bucket there is no horizontal room for a curve at all, so the
// link goes straight and the arrow turns with it.
export function linkGeometry(x1, y1, x2, y2) {
  const dx = x2 - x1;
  if (Math.abs(dx) < 10) {
    return { d: `M${x1} ${y1}L${x2} ${y2}`, angle: (Math.atan2(y2 - y1, dx) * 180) / Math.PI };
  }
  // Never past the midpoint: handles that overshoot each other put a wobble in
  // a link whose two cells are only a turn or two apart, which is most of them.
  const h = Math.min(Math.abs(dx) / 2, Math.max(14, Math.min(90, Math.abs(dx) * 0.45)));
  return { d: `M${x1} ${y1}C${x1 + h} ${y1} ${x2 - h} ${y2} ${x2} ${y2}`, angle: 0 };
}

// Which links to draw when there are more than the cap allows: keep the hardest
// claims, then restore weakest-first so the strongest still paint last, on top.
export function capLinks(links, cap = INFLUENCE_DRAW_CAP) {
  if (links.length <= cap) return { links, dropped: 0 };
  const kept = [...links]
    .sort((a, b) => (TIER_STRENGTH[a.tier] ?? 9) - (TIER_STRENGTH[b.tier] ?? 9))
    .slice(0, cap)
    .reverse();
  return { links: kept, dropped: links.length - kept.length };
}

// The episode the pointer is inside: the prompt turns bracketing it. An open end
// means the pointer is before the first prompt or after the last, and the guides
// run to the axis edge rather than stopping at a boundary that is not there.
export function episodeBracket(promptTurns, hoverTurn) {
  if (hoverTurn == null) return { prev: null, next: null };
  let prev = null;
  let next = null;
  for (const t of promptTurns || []) {
    if (t <= hoverTurn) prev = t;
    else {
      next = t;
      break;
    }
  }
  return { prev, next };
}
